/**
 * REST client for the artium-tasks service (the Core System workflow engine)
 * — RM Dashboard and friends. Cross-origin: the service allowlists CMS
 * origins via CORS. Auth is Authorization: Bearer <token> — the same
 * CMS-issued JWT the WebAPI interceptor already attaches (App.tsx), which
 * artium-tasks' JwtOrApiKeyGuard verifies independently with the shared
 * JWT_SECRET_KEY/JWT_CUSTOM_KEY every other Artium microservice trusts (see
 * artium-new-utility-service/middleware/check_token.js). X-Api-Key is for
 * server-to-server callers only (e.g. the core system) and isn't used here.
 */
import { currentAgentId } from '../Telephony/TelephonyProvider';

const TASKS_BASEURL = (
  process.env.REACT_APP_TASKS_BASEURL || 'http://localhost:3300'
).replace(/\/$/, '');

/**
 * Per-environment kill switch for the RM/Tasks feature (RM Dashboard, My
 * Learners, Renewals, Tasks) — on for local dev and uat, off for beta/prod
 * until this branch's rollout is ready there. Same pattern as
 * TELEPHONY_ENABLED in TelephonyProvider.tsx.
 */
export const RM_TASKS_ENABLED = process.env.REACT_APP_RM_TASKS_ENABLED === 'true';

export async function tasksApi<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const rawToken = localStorage.getItem('authToken') || '';
  const authHeader = rawToken
    ? rawToken.startsWith('Bearer ')
      ? rawToken
      : `Bearer ${rawToken}`
    : '';

  const res = await fetch(`${TASKS_BASEURL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(authHeader ? { Authorization: authHeader } : {}),
      ...(options.headers || {}),
    },
  });

  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const message = Array.isArray((body as any).message)
      ? (body as any).message.join(', ')
      : (body as any).message || res.statusText;
    const err = new Error(message) as Error & { status?: number };
    err.status = res.status; // let callers branch on 404/403 etc.
    throw err;
  }
  return body as T;
}

/**
 * Who is acting, sent on every mutation so the task timeline names the RM.
 * externalId = CMS userId — the shared identity across CMS/telephony/tasks.
 * role reads the cached /agents/me result (populated by an earlier
 * fetchTasksMe() call — every page that can trigger a mutation fetches it on
 * mount) so a TEAM_LEADER/ADMIN's override transitions actually carry their
 * real role instead of always claiming AGENT and getting rejected.
 */
export function currentActor(): {
  externalId?: string;
  name?: string;
  email?: string;
  role: TasksRole;
} {
  let name: string | undefined;
  let email: string | undefined;
  try {
    // profileData is written by the header's profile fetch and carries
    // firstName/lastName (no combined name field).
    const profile = JSON.parse(localStorage.getItem('profileData') || '{}');
    email = profile?.emailId || undefined;
    name =
      `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() ||
      email ||
      undefined;
  } catch {
    /* profileData is optional */
  }
  return {
    externalId: currentAgentId() || undefined,
    name,
    email,
    role: cachedMe?.value?.role ?? 'AGENT',
  };
}

/**
 * The `actor` body field for task mutations. Strictly the fields ActorDto
 * whitelists — the service rejects unknown properties, so never send the
 * whole currentActor() (its email is for agent self-provisioning only).
 */
export function actorPayload(): { externalId?: string; name?: string; role: TasksRole } {
  const { externalId, name, role } = currentActor();
  return { externalId, name, role };
}

// ---------------------------------------------------------------------------
// Tasks-service role (team leader vs agent)
// ---------------------------------------------------------------------------

export type TasksRole = 'AGENT' | 'TEAM_LEADER' | 'ADMIN';

export interface TasksMe {
  externalId: string;
  name: string;
  photoUrl?: string | null;
  role: TasksRole;
  team?: { key: string; name: string } | null;
  workingDays?: string[];
}

let cachedMe: { value: TasksMe | null } | null = null;

/**
 * The caller's own agent record in the tasks service — role/team/roster.
 * null when the logged-in user isn't provisioned as an agent. Cached for the
 * page's lifetime (role doesn't change mid-session).
 */
export async function fetchTasksMe(): Promise<TasksMe | null> {
  if (cachedMe) return cachedMe.value;
  try {
    const { agent } = await tasksApi<{ agent: TasksMe | null }>('/v1/agents/me');
    if (agent) {
      cachedMe = { value: agent };
      return agent;
    }
    // Fallback: /agents/me identifies the caller from the CMS JWT, which a
    // local (unauthenticated) API can't read. Resolve by the current user's
    // externalId instead so leader detection works in every environment.
    const myId = currentActor().externalId;
    if (myId) {
      const all = await tasksApi<TasksMe[]>('/v1/agents');
      const mine = all.find((a) => a.externalId === myId) ?? null;
      cachedMe = { value: mine };
      return mine;
    }
    cachedMe = { value: null };
    return null;
  } catch {
    cachedMe = { value: null };
    return null;
  }
}

/**
 * Drop the cached /agents/me result so the next fetchTasksMe() re-fetches —
 * call after the user edits their own agent record (e.g. a photo upload), or
 * the stale cache would keep serving the pre-edit values for the page's life.
 */
export function invalidateTasksMe(): void {
  cachedMe = null;
}

/** Patch the cached /agents/me in place (keeps the header fresh without a refetch). */
export function patchCachedTasksMe(patch: Partial<TasksMe>): void {
  if (cachedMe?.value) cachedMe.value = { ...cachedMe.value, ...patch };
}

/** True when the current user leads a team (or is admin) in the tasks service. */
export function isTasksLeaderRole(role: TasksRole | undefined | null): boolean {
  return role === 'TEAM_LEADER' || role === 'ADMIN';
}

// ---------------------------------------------------------------------------
// Agent availability (working days + per-day hours in IST)
// ---------------------------------------------------------------------------

export const WEEK_DAYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export type WorkingHours = Record<string, { start: string; end: string }>;
/** Fallback window used for a working day that has no explicit hours set. */
export const DEFAULT_WORKING_HOURS = { start: '10:00', end: '19:00' };

/** "HH:MM" → minutes since midnight (NaN-safe). */
const toMinutes = (hhmm: string): number => {
  const [h, m] = (hhmm || '').split(':').map(Number);
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const WEEK_ORDER = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

/**
 * Whether an agent is on the clock right now, evaluated in IST (Asia/Kolkata).
 * Off if today isn't a working day (and no overnight shift from yesterday is
 * still running); otherwise checks the day's hours (falling back to
 * DEFAULT_WORKING_HOURS when a working day has no explicit window).
 *
 * An overnight window (start > end, e.g. 23:00–08:00 — international-timezone
 * RMs run these) is split across the two calendar days it spans: today's
 * entry only supplies the evening leg (from `start` onward); the
 * early-morning leg before `end` belongs to *yesterday's* entry. Folding both
 * legs into "today's window" would make a shift that hasn't started yet this
 * evening (e.g. Monday 2am, with Sunday off) read as already available.
 */
export function agentAvailability(
  workingDays?: string[] | null,
  workingHours?: WorkingHours | null,
): { available: boolean; label: string; hours: { start: string; end: string } | null } {
  // A Date whose *local* fields read as the current IST wall-clock time.
  const ist = new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const dayKey = WEEK_ORDER[ist.getDay()];
  const days = workingDays ?? [];
  const nowM = ist.getHours() * 60 + ist.getMinutes();
  const isWorkingToday = days.includes(dayKey);
  const todayHrs = workingHours?.[dayKey] ?? DEFAULT_WORKING_HOURS;
  const todayStart = toMinutes(todayHrs.start);
  const todayEnd = toMinutes(todayHrs.end);

  if (isWorkingToday) {
    const crossesMidnight = todayStart > todayEnd;
    const onTodaysEveningLeg = crossesMidnight ? nowM >= todayStart : nowM >= todayStart && nowM < todayEnd;
    if (onTodaysEveningLeg) {
      // Past midnight the end time lands on tomorrow — say so, or "until
      // 08:00 IST" reads as later today when it's actually already gone.
      const dayNote = crossesMidnight ? ` (${WEEK_ORDER[(WEEK_ORDER.indexOf(dayKey) + 1) % 7]})` : '';
      return { available: true, label: `Available · until ${todayHrs.end} IST${dayNote}`, hours: todayHrs };
    }
  }

  const yesterdayKey = WEEK_ORDER[(WEEK_ORDER.indexOf(dayKey) + 6) % 7];
  if (days.includes(yesterdayKey)) {
    const yHrs = workingHours?.[yesterdayKey] ?? DEFAULT_WORKING_HOURS;
    const yStart = toMinutes(yHrs.start);
    const yEnd = toMinutes(yHrs.end);
    if (yStart > yEnd && nowM < yEnd) {
      return { available: true, label: `Available · until ${yHrs.end} IST`, hours: yHrs };
    }
  }

  if (!isWorkingToday) return { available: false, label: 'Off today', hours: null };
  return { available: false, label: `Off · ${todayHrs.start}–${todayHrs.end} IST`, hours: todayHrs };
}

/**
 * Effective roles a provisioned role can act as — mirrors the task service's
 * `resolveActor`, so the CMS shows exactly the transitions the backend accepts.
 * ADMIN can act as everyone (incl. SYSTEM, for event-driven overrides); a
 * TEAM_LEADER is a leader only; anyone else (or unprovisioned) is an AGENT.
 */
export function effectiveTaskRoles(role: TasksRole | undefined | null): string[] {
  if (role === 'ADMIN') return ['AGENT', 'TEAM_LEADER', 'SYSTEM'];
  if (role === 'TEAM_LEADER') return ['TEAM_LEADER'];
  return ['AGENT'];
}

/** Whether the given effective roles may perform a transition (empty = open). */
export function canPerformTransition(
  allowedRoles: string[],
  effectiveRoles: string[],
): boolean {
  return allowedRoles.length === 0 || allowedRoles.some((r) => effectiveRoles.includes(r));
}

// ---------------------------------------------------------------------------
// Types (mirror artium-tasks' API responses)
// ---------------------------------------------------------------------------

export interface TaskStatusCfg {
  id: string;
  key: string;
  label: string;
  sortOrder: number;
  isInitial: boolean;
  isTerminal: boolean;
  isSuccess: boolean;
  /** Happy-path stage (rendered as a chevron) vs parking/failure side state. */
  isOnPath: boolean;
}

export interface TaskTransitionCfg {
  id: string;
  fromStatusId: string;
  toStatusId: string;
  allowedRoles: string[];
  requiresGoalMet: boolean;
  requiresNote: boolean;
  fromStatus: TaskStatusCfg;
  toStatus: TaskStatusCfg;
}

export interface TaskTypeCfg {
  id: string;
  key: string;
  name: string;
  description: string | null;
  goalKey: string;
  goalDescription: string;
  defaultPriority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  /** Manual outreach outcomes from the RM catalog ("Whatsapp sent"…). */
  callStatuses: string[];
  /** Sub-reasons required when declining (empty = free-text note only). */
  declinedReasons: string[];
  statuses: TaskStatusCfg[];
  transitions: TaskTransitionCfg[];
  /**
   * How many tickets this type holds, ever — all stages, all dates. The
   * catalog is seeded well before the flows that fill it, so a type sitting
   * at 0 has no work to filter and is left out of the filter dropdown.
   */
  taskCount?: number;
  /** LEGACY = status chain + transitions; TICKET = Open/In Progress/Closed. */
  model?: 'LEGACY' | 'TICKET';
  ticketConfig?: TicketConfig | null;
}

// ---------------------------------------------------------------------------
// Unified ticket model (task types with model === 'TICKET')
// ---------------------------------------------------------------------------

export type TaskStage = 'OPEN' | 'IN_PROGRESS' | 'CLOSED';

export const STAGE_LABEL: Record<TaskStage, string> = {
  OPEN: 'Open',
  IN_PROGRESS: 'In Progress',
  CLOSED: 'Closed',
};

export interface TicketOption {
  key: string;
  label: string;
}

export interface TicketFieldDef {
  key: string;
  label: string;
  kind: 'select' | 'multi' | 'text' | 'date' | 'datetime';
  options?: TicketOption[];
  /** Cascade: only asked while `field`'s answer is one of `in`. */
  showWhen?: { field: string; in: string[] };
  /** Activity keys that ask it, or `res:<resolutionKey>` for a close. */
  askAt: string[];
  required?: boolean;
  /** `date` only: how far ahead the answer may sit, in months from today. */
  maxMonthsAhead?: number;
  /** Narrows `required` to some of the activities that ask it. */
  requiredAt?: string[];
  /** Show this cascade answer on its own summary line, not folded into its parent's. */
  line?: boolean;
}

export interface TicketResolution {
  key: string;
  label: string;
  tone: 'success' | 'fail' | 'neutral';
  /** Kept so old tickets still read right, but no longer offered. */
  retired?: boolean;
  requires?: string[];
  /** Normally set by the system; an RM may still pick it as a fallback. */
  systemOutcome?: boolean;
  spawnsFollowOn?: { dateField: string };
}

export interface TicketActivity {
  key: string;
  label: string;
  /**
   * Whether logging it asks "when to follow up", setting the ticket's
   * `followUpAt`. 'required' for the follow-up itself; 'optional' where it is
   * a reminder the RM may set in passing, such as on an attempt nobody
   * answered.
   */
  followUp?: 'required' | 'optional';
}

export interface TicketConfig {
  activities: TicketActivity[];
  resolutions: TicketResolution[];
  fields: TicketFieldDef[];
  goalResolutionKey?: string;
}

export type FieldValues = Record<string, string | string[]>;

/** The activity that sets the follow-up time. */
export const FOLLOW_UP_ACTIVITY = 'followup';

export const isTicketType = (cfg: TaskTypeCfg | undefined | null): cfg is TaskTypeCfg & { ticketConfig: TicketConfig } =>
  !!cfg && cfg.model === 'TICKET' && !!cfg.ticketConfig;

export const isEmptyAnswer = (v: unknown): boolean =>
  v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

/** Mirrors the service: a cascade field shows only while its parent picks it. */
export function fieldVisible(cfg: TicketConfig, field: TicketFieldDef, values: FieldValues, depth = 0): boolean {
  if (!field.showWhen) return true;
  if (depth > 10) return false;
  const parent = cfg.fields.find((f) => f.key === field.showWhen!.field);
  if (parent && !fieldVisible(cfg, parent, values, depth + 1)) return false;
  const v = values[field.showWhen.field];
  if (Array.isArray(v)) return v.some((x) => field.showWhen!.in.includes(x));
  return typeof v === 'string' && field.showWhen.in.includes(v);
}

export function pruneHiddenAnswers(cfg: TicketConfig, values: FieldValues): FieldValues {
  const next = { ...values };
  for (let pass = 0; pass < 4; pass++) {
    cfg.fields.forEach((f) => {
      if (f.showWhen && !fieldVisible(cfg, f, next)) delete next[f.key];
    });
  }
  return next;
}

/** Nesting depth of a cascade field (0 = top level). */
export function fieldDepth(cfg: TicketConfig, field: TicketFieldDef): number {
  let depth = 0;
  let parentKey = field.showWhen?.field;
  while (parentKey && depth < 10) {
    depth += 1;
    const key: string = parentKey;
    parentKey = cfg.fields.find((f) => f.key === key)?.showWhen?.field;
  }
  return depth;
}

const optionLabel = (field: TicketFieldDef, key: string): string =>
  field.options?.find((o) => o.key === key)?.label ?? key;

export function displayAnswer(field: TicketFieldDef, value: string | string[]): string {
  if (Array.isArray(value)) return value.map((v) => optionLabel(field, v)).join(', ');
  if (field.kind === 'select') return optionLabel(field, value);
  if (field.kind === 'date') {
    const d = new Date(`${value}T00:00:00`);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  if (field.kind === 'datetime') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  }
  return value;
}

/**
 * Answers as display lines, with each cascade folded into its parent's line:
 * "Reason for not taking classes: Personal reasons › Travelling".
 */
export function summarizeAnswers(cfg: TicketConfig, values: FieldValues): { label: string; value: string }[] {
  const chain = (field: TicketFieldDef): string => {
    let text = displayAnswer(field, values[field.key]);
    cfg.fields.forEach((child) => {
      if (
        child.showWhen?.field === field.key &&
        !child.line &&
        fieldVisible(cfg, child, values) &&
        !isEmptyAnswer(values[child.key])
      ) {
        text += ` › ${chain(child)}`;
      }
    });
    return text;
  };
  return cfg.fields
    .filter((f) => (!f.showWhen || f.line) && fieldVisible(cfg, f, values) && !isEmptyAnswer(values[f.key]))
    .map((f) => ({ label: f.label, value: chain(f) }));
}

export interface TaskAgent {
  id: string;
  externalId: string;
  name: string;
  email: string;
  role: string;
  photoUrl?: string | null;
}

/** Human-friendly display id: taskNumber 1 → "RMT-0001". */
export const formatTaskId = (taskNumber: number): string =>
  `RMT-${String(taskNumber).padStart(4, '0')}`;

export interface Task {
  id: string;
  /** Sequential serial behind the RMT-#### display id. */
  taskNumber: number;
  customerType: 'STUDENT' | 'TEACHER';
  customerExternalId: string;
  customerName: string;
  customerPhoneE164: string | null;
  customerEmail: string | null;
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  /** Action-by deadline — the sheet's Agent SLA, counted in the RM's shift. */
  dueAt: string | null;
  /** Close-by deadline — the sheet's Resolution Time, plain calendar hours. */
  resolveDueAt?: string | null;
  /** The learner's timezone, from their record — what "their local time" uses. */
  customerTimezone?: string | null;
  goalMetAt: string | null;
  /**
   * When an RM first worked the ticket. The sheet's SLA is "actioned within
   * N hours", so this — not closedAt — is what adherence is measured against.
   */
  firstResponseAt?: string | null;
  followUpAt: string | null;
  followUpNote: string | null;
  closedAt: string | null;
  resolution: 'SUCCESS' | 'FAILURE' | 'CANCELLED' | null;
  context: Record<string, unknown>;
  createdAt: string;
  /** Same three stages for every type (derived from the status on LEGACY types). */
  stage?: TaskStage;
  /**
   * Warehouse facts about the learner's enrolment, limited to the properties
   * ops have marked visible. Absent when the ticket carries no enrolment in
   * its key, or the warehouse has not reached that learner yet — so always
   * optional, per key as well as per ticket.
   */
  facts?: Record<string, string | number | boolean | null>;
  /** TICKET model: the closing reason's key once closed. */
  resolutionKey?: string | null;
  /** TICKET model: answers keyed by field key (option keys, never labels). */
  fieldValues?: FieldValues;
  lastActivityKey?: string | null;
  /**
   * Every activity ever logged on the ticket, not only the latest. Lets the
   * queue answer "did an RM ever get through" — the question Renewal Insights
   * answers — which lastActivityKey alone cannot.
   */
  activitiesLogged?: string[];
  lastActivityAt?: string | null;
  taskType: { id: string; key: string; name: string; goalDescription: string };
  status: TaskStatusCfg;
  team: { id: string; key: string; name: string } | null;
  assignee: TaskAgent | null;
}

export interface TaskEvent {
  id: string;
  type: string;
  actorType: 'AGENT' | 'SYSTEM' | 'API';
  actorExternalId: string | null;
  actorName: string | null;
  note: string | null;
  payload: Record<string, any>;
  createdAt: string;
}

export interface TaskCall {
  id: string;
  telephonyCallId: string;
  direction: string;
  customerE164: string;
  callStatus: string;
  disposition: string | null;
  talkSeconds: number | null;
  occurredAt: string;
  recordingAvailable: boolean;
  recordingId: string | null;
}

/** A linked call's recording + transcript, resolved on demand from telephony. */
export interface CallRecording {
  recordingId: string;
  status: string | null;
  durationSec: number | null;
  format: string | null;
  playbackUrl: string | null;
  transcript: string | null;
  transcriptStatus: string | null;
  transcribedAt: string | null;
}

export interface TaskDetail extends Task {
  events: TaskEvent[];
  calls: TaskCall[];
}

export type HealthBand = 'HEALTHY' | 'WATCHLIST' | 'AT_RISK' | 'CRITICAL';

/** GET /v1/agents/:externalId/portfolio-health — the RM's learner health. */
/** The components behind a learner's total health score. */
export interface HealthBreakdown {
  engagement: number | null;
  coursePace: number | null;
  teacherNoShows: number | null;
  learnerNoShows: number | null;
  renewal: number | null;
  teacherChange: number | null;
  classRatings: number | null;
  npsScore: number | null;
}

/** Display order + labels for the breakdown popover. */
export const HEALTH_COMPONENT_LABELS: { key: keyof HealthBreakdown; label: string }[] = [
  { key: 'engagement', label: 'Engagement' },
  { key: 'coursePace', label: 'Course pace' },
  { key: 'teacherNoShows', label: 'Teacher no-shows' },
  { key: 'learnerNoShows', label: 'Learner no-shows' },
  { key: 'renewal', label: 'Renewal' },
  { key: 'teacherChange', label: 'Teacher change' },
  { key: 'classRatings', label: 'Class ratings' },
  { key: 'npsScore', label: 'NPS score' },
];

export interface PortfolioHealth {
  agent: { externalId: string; name: string };
  learners: {
    studentExternalId: string;
    name: string;
    phoneE164: string | null;
    email: string | null;
    /** Core-system deep-link ids from the mapping (credentialId, studentId…). */
    metadata: Record<string, unknown>;
    /** RM-pinned labels ("VIP", "IRATE USER", "DND"…). */
    tags: string[];
    /** Student's own IANA timezone, when the core system has sent it. */
    timezone?: string | null;
    /** Country NAME ("India"), timezone-derived upstream — trust it over
     * the phone prefix (US students carry Indian numbers). */
    countryTzone?: string | null;
    score: number | null;
    band: HealthBand | null;
    period: string | null;
    /** Latest row's per-component values; null components weren't reported. */
    breakdown?: HealthBreakdown | null;
    /** Owning RM — present only on the team-wide (leader) portfolio. */
    agent?: { externalId: string; name: string };
  }[];
  averageScore: number | null;
  previousAverageScore: number | null;
  bands: Record<HealthBand, number>;
  monthly: { month: string; average: number }[];
}

// ---------------------------------------------------------------------------
// Team overview (leader dashboard)
// ---------------------------------------------------------------------------

export interface AgentMetrics {
  externalId: string;
  name: string;
  photoUrl?: string | null;
  team: { key: string; name: string } | null;
  students: number;
  openTasks: number;
  dueToday: number;
  /** Action SLA missed: untouched past its deadline. */
  overdue: number;
  /** Being worked, but past the day it should have been resolved. */
  pastResolution: number;
  renewalsOpen: number;
  onboardingOpen: number;
  closed7d: number;
  bands: Record<HealthBand, number>;
  avgScore: number | null;
  workingDays?: string[];
  workingHours?: WorkingHours;
}

export type TrendGranularity = 'day' | 'week' | 'month';

export interface TaskTrends {
  granularity: TrendGranularity;
  types: string[];
  series: { period: string; label: string; counts: Record<string, number> }[];
}

export type HealthGranularity = 'week' | 'month';

export interface HealthTrends {
  granularity: HealthGranularity;
  series: { period: string; label: string; average: number | null; students: number }[];
}

export interface AgentHealthTrends {
  granularity: 'week';
  agents: { externalId: string; name: string }[];
  series: { period: string; label: string; byAgent: Record<string, number | null> }[];
}

export interface TeamOverview {
  totals: Omit<AgentMetrics, 'externalId' | 'name' | 'team'>;
  agents: AgentMetrics[];
}

// ---------------------------------------------------------------------------
// Customer local time
// ---------------------------------------------------------------------------

/** Country-code → timezone guesses, longest prefix first. approx marks
 * multi-zone countries (a +1 number could be any US/CA zone). */
const CC_TIMEZONES: { prefix: string; tz: string; label: string; iso: string; country: string; approx?: boolean }[] = [
  { prefix: '+971', tz: 'Asia/Dubai', label: 'GST', iso: 'ae', country: 'UAE' },
  { prefix: '+974', tz: 'Asia/Qatar', label: 'AST', iso: 'qa', country: 'Qatar' },
  { prefix: '+966', tz: 'Asia/Riyadh', label: 'AST', iso: 'sa', country: 'Saudi Arabia' },
  { prefix: '+965', tz: 'Asia/Kuwait', label: 'AST', iso: 'kw', country: 'Kuwait' },
  { prefix: '+91', tz: 'Asia/Kolkata', label: 'IST', iso: 'in', country: 'India' },
  { prefix: '+65', tz: 'Asia/Singapore', label: 'SGT', iso: 'sg', country: 'Singapore' },
  { prefix: '+61', tz: 'Australia/Sydney', label: 'AET', iso: 'au', country: 'Australia', approx: true },
  { prefix: '+64', tz: 'Pacific/Auckland', label: 'NZT', iso: 'nz', country: 'New Zealand' },
  { prefix: '+44', tz: 'Europe/London', label: 'UK', iso: 'gb', country: 'United Kingdom' },
  { prefix: '+49', tz: 'Europe/Berlin', label: 'CET', iso: 'de', country: 'Germany' },
  { prefix: '+33', tz: 'Europe/Paris', label: 'CET', iso: 'fr', country: 'France' },
  { prefix: '+1', tz: 'America/New_York', label: 'US ET', iso: 'us', country: 'US/Canada', approx: true },
];

/** ISO flag code + country name from the phone's country code (flag-icons
 * classes `fi fi-<iso>` are loaded globally via app.scss). */
export function customerCountry(
  phoneE164: string | null | undefined,
): { iso: string; country: string } | null {
  const hit = CC_TIMEZONES.find((c) => (phoneE164 ?? '').startsWith(c.prefix));
  return hit ? { iso: hit.iso, country: hit.country } : null;
}

/** Country names (as the core system writes them) → flag ISO codes. */
const COUNTRY_NAME_ISO: Record<string, string> = {
  india: 'in',
  us: 'us', usa: 'us', 'united states': 'us', 'united states of america': 'us',
  canada: 'ca',
  uk: 'gb', 'united kingdom': 'gb', 'great britain': 'gb', england: 'gb',
  uae: 'ae', 'united arab emirates': 'ae',
  singapore: 'sg',
  australia: 'au',
  'new zealand': 'nz',
  qatar: 'qa', 'saudi arabia': 'sa', kuwait: 'kw',
  germany: 'de', france: 'fr',
};

/**
 * A learner's country for flag display. The student_details country name
 * (timezone-derived upstream) wins over the phone prefix — a US student
 * with an Indian number must show the US flag.
 */
export function learnerCountry(
  countryName: string | null | undefined,
  phoneE164: string | null | undefined,
): { iso: string; country: string } | null {
  const name = (countryName ?? '').trim();
  if (name) {
    const iso = COUNTRY_NAME_ISO[name.toLowerCase()];
    if (iso) return { iso, country: name };
  }
  return customerCountry(phoneE164);
}

/**
 * The customer's current local time, for "is it a good moment to call".
 * Prefers an explicit context.timezone (IANA name from the core system);
 * falls back to the phone country code. Null when neither is usable.
 */
/** Is it polite to call at this local hour? 9 AM–9 PM = good, the shoulder
 * hours (8–9 AM, 9–10 PM) = caution, overnight = avoid. */
export type CallWindow = 'GOOD' | 'CAUTION' | 'AVOID';

const windowOf = (hour: number): CallWindow => {
  if (hour >= 9 && hour < 21) return 'GOOD';
  if (hour === 8 || hour === 21) return 'CAUTION';
  return 'AVOID';
};

export function customerLocalTime(
  task: {
    context: Record<string, unknown>;
    customerPhoneE164: string | null;
    /** The learner's own timezone, from their record. Trust this first. */
    customerTimezone?: string | null;
  },
  at: Date = new Date(),
): { time: string; label: string; approx: boolean; window: CallWindow } | null {
  const build = (tz: string, fallbackLabel: string, approx: boolean) => {
    // Explicit uppercase AM/PM so the hour reads unambiguously at a glance.
    const time = at
      .toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', hour12: true })
      .toUpperCase();
    const hour = Number(
      at.toLocaleTimeString('en-GB', { timeZone: tz, hour: '2-digit', hour12: false }).slice(0, 2),
    );
    // Second row shows the ZONE, not a city — cities aren't always known,
    // the timezone always is: "IST", "EDT", "AEST", or "GMT+4".
    let label = fallbackLabel;
    try {
      label =
        new Intl.DateTimeFormat('en-IN', { timeZone: tz, timeZoneName: 'short' })
          .formatToParts(at)
          .find((part) => part.type === 'timeZoneName')?.value ?? fallbackLabel;
    } catch {
      /* keep fallback */
    }
    return { time, label, approx, window: windowOf(hour) };
  };

  // The learner's stored zone is the real answer; the phone's country code
  // is a guess, and +1 alone spans four US zones — it read a Portland
  // learner as New York, three hours out.
  // No Region/City test here: a few learner records carry an older-style
  // name like "EST5EDT", which is a real zone Intl accepts. Hand anything
  // non-empty to build() and let it throw if the name is junk.
  const known = task.customerTimezone;
  if (typeof known === 'string' && known.trim()) {
    try {
      return build(known, (known.split('/').pop() ?? known).replace(/_/g, ' '), false);
    } catch {
      /* unknown zone name — fall through */
    }
  }

  const ctxTz = task.context?.timezone;
  if (typeof ctxTz === 'string' && ctxTz.includes('/')) {
    try {
      return build(ctxTz, (ctxTz.split('/').pop() ?? ctxTz).replace(/_/g, ' '), false);
    } catch {
      /* invalid zone name — fall through to the phone heuristic */
    }
  }

  const phone = task.customerPhoneE164 ?? '';
  const hit = CC_TIMEZONES.find((c) => phone.startsWith(c.prefix));
  if (!hit) return null;
  try {
    return build(hit.tz, hit.label, !!hit.approx);
  } catch {
    return null;
  }
}

export const CALL_WINDOW_STYLE: Record<CallWindow, { color: string; label: string }> = {
  GOOD: { color: '#16a34a', label: 'Good time to call' },
  CAUTION: { color: '#b45309', label: 'Early/late — call only if urgent' },
  AVOID: { color: '#dc2626', label: 'Outside calling hours — do not disturb' },
};

/* ── Monthly AI summaries ─────────────────────────────────────────────────
 * One row per student per month, generated from their support history
 * across Freshdesk, Freshchat, calls, PTM notes and class ratings. Agents
 * read it before calling, so they walk in knowing the month's story.
 */

export type StudentSentiment = 'POSITIVE' | 'NEUTRAL' | 'NEGATIVE' | 'MIXED';

/** One channel's contribution: how much there was, and what it said. */
export interface ChannelInsight {
  count?: number;
  /** null when that channel had nothing that month — never guessed. */
  summary?: string | null;
}

export interface AiSummary {
  id: string;
  studentExternalId: string;
  /** First day of the month the summary covers. */
  period: string;
  sentiment: StudentSentiment;
  summary: string;
  agentFocusAreas: string[];
  positiveHighlights: string[];
  openConcerns: string[];
  channelInsights: Record<string, ChannelInsight>;
  model: string;
  generatedAt: string;
}

export const SENTIMENT_STYLE: Record<StudentSentiment, { label: string; fg: string; bg: string }> = {
  POSITIVE: { label: 'Positive', fg: '#166534', bg: '#e8f6ee' },
  NEUTRAL: { label: 'Neutral', fg: '#45536b', bg: '#eef1f5' },
  MIXED: { label: 'Mixed', fg: '#92400e', bg: '#fef3c7' },
  NEGATIVE: { label: 'Needs attention', fg: '#9f1c1c', bg: '#fdecec' },
};

/** Channel keys as the pipeline sends them, in the order agents care about. */
export const CHANNEL_LABELS: Record<string, string> = {
  freshdesk: 'Support tickets',
  freshchat: 'Chat',
  telephonyCalls: 'Calls',
  ptmNotes: 'PTM notes',
  classRatings: 'Class ratings',
};

/** "2026-09-01" → "September 2026". */
export const summaryMonth = (period: string): string =>
  new Date(period).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/**
 * Did this month actually have anything behind it? The pipeline writes a
 * row per student per month either way, and an empty one carries NEUTRAL
 * sentiment with no channels — which reads as an assessment when it only
 * means "we saw nothing".
 */
export const summaryHasSubstance = (row: AiSummary): boolean =>
  !!row.agentFocusAreas?.length ||
  !!row.openConcerns?.length ||
  !!row.positiveHighlights?.length ||
  Object.values(row.channelInsights ?? {}).some((c) => !!c?.summary || (c?.count ?? 0) > 0);

/**
 * Per-student cache for the session. A ticket queue asks for the same
 * student repeatedly — opening a ticket, closing the panel, reopening it —
 * and the summary only changes once a month.
 */
const summaryCache = new Map<string, Promise<AiSummary[]>>();

/** A student's summaries, newest month first. Empty when none exist yet. */
export function fetchAiSummaries(studentExternalId: string): Promise<AiSummary[]> {
  const cached = summaryCache.get(studentExternalId);
  if (cached) return cached;

  const request = tasksApi<AiSummary[]>(
    `/v1/students/${encodeURIComponent(studentExternalId)}/ai-summaries`,
  )
    .then((rows) => (Array.isArray(rows) ? rows : []))
    .catch((error: { status?: number }) => {
      // No summary yet is the normal case for a new student, not a failure.
      if (error?.status === 404) return [] as AiSummary[];
      summaryCache.delete(studentExternalId); // let a real failure be retried
      throw error;
    });

  summaryCache.set(studentExternalId, request);
  return request;
}

/* ── Agent × task-type counts (RM dashboard) ─────────────────────────────
 * Counted server-side. The dashboard used to fetch rows and tally them,
 * which truncated at this API's 200-row page size.
 */

export interface AgentTypeCounts {
  taskTypeKey: string;
  taskTypeName: string;
  open: number;
  inProgress: number;
  /** Action SLA missed: untouched past its deadline. */
  overdue: number;
  /** Being worked, but past the day it should have been resolved. */
  pastResolution: number;
  closed: number;
}

export interface AgentTypeBreakdown {
  from: string | null;
  to: string | null;
  timeZone: string;
  agents: {
    agentExternalId: string | null;
    agentName: string;
    types: AgentTypeCounts[];
    openTotal: number;
    inProgressTotal: number;
    overdueTotal: number;
    pastResolutionTotal: number;
    closedTotal: number;
  }[];
}

/** Quick presets; ops can also pick exact dates. */
export const BREAKDOWN_RANGES = [
  { key: 'all', label: 'All time', days: null },
  { key: 'month', label: 'This month', days: null },
  { key: 'today', label: 'Today', days: 0 },
  { key: 'yesterday', label: 'Yesterday', days: 1 },
  { key: '7', label: 'Last 7 days', days: 7 },
  { key: '30', label: 'Last 30 days', days: 30 },
  { key: 'custom', label: 'Custom…', days: null },
] as const;

export type BreakdownRangeKey = (typeof BREAKDOWN_RANGES)[number]['key'];

/** An IST calendar day (the reporting day), as YYYY-MM-DD. */
export const istDay = (daysAgo = 0): string =>
  new Date(Date.now() + 5.5 * 3600000 - daysAgo * 86400000).toISOString().slice(0, 10);

/** The from/to a preset stands for; both empty means all time. */
export const rangeDates = (key: BreakdownRangeKey): { from: string; to: string } => {
  // The 1st of the current IST month to today — the breakdown's default,
  // because "all time" rescans every task ever raised on every load.
  if (key === 'month') return { from: `${istDay(0).slice(0, 7)}-01`, to: istDay(0) };
  const spec = BREAKDOWN_RANGES.find((r) => r.key === key);
  if (!spec || spec.days === null) return { from: '', to: '' };
  // Yesterday is that day alone, not "since yesterday".
  if (key === 'yesterday') return { from: istDay(1), to: istDay(1) };
  return { from: istDay(spec.days), to: istDay(0) };
};

/** Shift a range by its own length — the arrows for stepping day by day. */
export const shiftRange = (
  from: string,
  to: string,
  direction: -1 | 1,
): { from: string; to: string } => {
  const day = 86400000;
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  const span = b - a + day; // inclusive
  const iso = (t: number): string => new Date(t).toISOString().slice(0, 10);
  return { from: iso(a + direction * span), to: iso(b + direction * span) };
};

export async function fetchAgentTypeBreakdown(opts: {
  from?: string;
  to?: string;
}): Promise<AgentTypeBreakdown> {
  const params = new URLSearchParams();
  // Both ends, or neither — a half-open range reads ambiguously on screen.
  if (opts.from && opts.to) {
    params.set('from', opts.from);
    params.set('to', opts.to);
    params.set('tz', 'Asia/Kolkata');
  }
  const qs = params.toString();
  return tasksApi<AgentTypeBreakdown>(`/v1/team-overview/by-agent-type${qs ? `?${qs}` : ''}`);
}

/**
 * A warehouse property ops have chosen to show. The queue draws a column per
 * visible one, so turning a column on is a flag rather than a release.
 */
export interface FactProperty {
  id: string;
  key: string;
  scope: 'LEARNER' | 'ENROLLMENT';
  label: string;
  kind: string;
  isVisible: boolean;
  sortOrder: number;
}
